import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
type CostDimension = {
  index: string;
  label: string;
  value: string;
  caption: string;
  description: string[];
};
const dimensions: CostDimension[] = [{
  index: '01',
  label: 'Costo nascosto',
  value: '12%',
  caption: 'di sovrapprezzo medio non controllato che i corrieri aggiungono in fattura.',
  description: ['Supplementi carburante', 'Accessoriali non concordati', 'Riconsegne fatturate', 'Oneri di giacenza', 'Dimensioni e peso non verificati']
}, {
  index: '02',
  label: 'Costo operativo',
  value: '18%',
  caption: 'di spedizioni con anomalie diagnosticabili prima ancora che il pacco parta.',
  description: ['Riconsegne pagate due volte', 'Giacenze in deposito', 'Contatti ripetuti al customer care', 'Costi extra del corriere', 'Gestione dei resi']
}, {
  index: '03',
  label: 'Costo reputazionale',
  value: '−23%',
  caption: 'di valore percepito e clienti che non tornano quando l\u2019esperienza di consegna è scadente.',
  description: ['Mancato riacquisto', 'NPS in calo', 'Recensioni negative', 'Churn dei clienti fedeli', 'Erosione del valore percepito']
}];
// Spiegazione di ciascun costo nascosto sotto le chips
const chipExplanations: Record<string, string> = {
  // Costo nascosto
  'Supplementi carburante': 'I corrieri applicano un sovrapprezzo carburante che varia ogni mese, spesso senza preavviso. Può arrivare a rappresentare fino al 20% del netto fatturato e raramente viene verificato a fine periodo.',
  'Accessoriali non concordati': 'Voci come "ZTL", "consegna al piano" o "tentativo extra" finiscono in fattura fuori dal contratto negoziato. Senza un audit sistematico, restano invisibili.',
  'Riconsegne fatturate': 'Ogni tentativo di consegna viene addebitato, anche quando fallisce per cause imputabili al corriere. Il costo unitario raddoppia o triplica senza che il cliente lo sappia.',
  'Oneri di giacenza': 'Quando un pacco resta in deposito, scatta una tariffa giornaliera. Spesso non c\u2019è alcuna notifica al mittente: il costo si scopre solo a fattura emessa.',
  'Correzioni indirizzo': 'Ogni ricalcolo di indirizzo, anche per pochi caratteri sbagliati, comporta un addebito fisso. Sui grandi volumi diventa una voce significativa.',
  'Dimensioni e peso non verificati': 'Il corriere riconvalida le misure e applica supplementi sul "peso volumetrico". Senza una verifica indipendente, paghi spedizioni che pensavi pi\u00f9 leggere.',
  // Costo operativo
  'Riconsegne pagate due volte': 'Quando una consegna fallisce, il pacco viene rispedito: paghi due spedizioni per un solo ordine. Su scala, \u00e8 una delle voci pi\u00f9 erosive del margine.',
  'Giacenze in deposito': 'I pacchi fermi in deposito generano costi diretti (giacenza) e indiretti (ritardi a catena, customer care, resi). L\u2019impatto reale \u00e8 spesso 3\u20134x il costo nominale.',
  'Contatti ripetuti al customer care': 'Ogni "dov\u2019\u00e8 il mio ordine?" costa tra 5 e 15\u20ac tra tempo operatore e tool. Una delivery opaca moltiplica questi contatti.',
  'Costi extra del corriere': 'Surcharge non previsti per zone disagiate, festivi, ZTL, consegne urgenti. Senza monitoraggio, finiscono in fattura senza essere validati.',
  'Gestione dei resi': 'La logistica inversa \u2014 ritiro, ispezione, rimessa a magazzino, eventuale rimborso \u2014 pu\u00f2 erodere fino al 30% del valore dell\u2019ordine.',
  // Costo di brand
  'Mancato riacquisto': 'Un cliente che vive una brutta consegna ha fino al 60% di probabilit\u00e0 in meno di tornare. Il costo non \u00e8 nella spedizione: \u00e8 nel LTV perso.',
  'NPS in calo': 'La delivery \u00e8 il primo touchpoint post-acquisto e pesa pi\u00f9 di prodotto e packaging nei punteggi NPS. Una consegna scadente trascina gi\u00f9 tutto.',
  'Recensioni negative': 'Quasi il 40% delle recensioni a 1 stella riguarda la consegna, non il prodotto. Sono pubbliche, persistenti, e influenzano i nuovi clienti.',
  'Churn dei clienti fedeli': 'Anche un cliente top abbandona dopo 2\u20133 esperienze di consegna scadenti. \u00c8 il segmento pi\u00f9 costoso da perdere e il pi\u00f9 difficile da riconquistare.',
  'Erosione del valore percepito': 'Un packaging premium svanisce davanti a un corriere scortese o un pacco in ritardo. La percezione del brand si gioca nell\u2019ultimo miglio.',
  'Perdita di pricing power': 'Un brand percepito come "low-cost delivery" fatica a sostenere prezzi premium. La consegna detta il tetto del posizionamento.',
  'Posizionamento premium indebolito': '\u00c8 difficile mantenere un\u2019identit\u00e0 luxury o premium con un\u2019esperienza di consegna generica. La delivery o sostiene la promessa del brand, o la smentisce.'
};
type Tint = {
  background: string;
  borderColor: string;
  numberClass: string;
  boxShadow: string;
};
const tints: Tint[] = [{
  background: '#000000',
  borderColor: 'rgba(255,255,255,0.18)',
  numberClass: 'color-ink-tertiary',
  boxShadow: '0 24px 60px rgba(0,0,0,0.5)'
}, {
  background: '#000000',
  borderColor: 'rgba(255,255,255,0.18)',
  numberClass: 'color-ink-tertiary',
  boxShadow: '0 24px 60px rgba(0,0,0,0.5)'
}, {
  background: '#000000',
  borderColor: 'rgba(255,255,255,0.18)',
  numberClass: 'color-ink-tertiary',
  boxShadow: '0 24px 60px rgba(0,0,0,0.5)'
}];
type ActiveChip = {
  category: string;
  chip: string;
} | null;
interface ProblemCardProps {
  d: CostDimension;
  i: number;
  onChipClick: (category: string, chip: string) => void;
}
function ProblemCard({
  d,
  i,
  onChipClick
}: ProblemCardProps) {
  const tint = tints[i] || tints[0];
  return <motion.article initial={{
    opacity: 0
  }} whileInView={{
    opacity: 1
  }} viewport={{
    once: true,
    amount: 0.3
  }} transition={{
    duration: 0.5,
    delay: i * 0.2
  }} style={{
    background: tint.background,
    borderColor: tint.borderColor,
    boxShadow: tint.boxShadow
  }} className="rounded-2xl border p-6 lg:p-8 flex flex-col h-full" data-id="element-282">
      {/* Top row: number */}
      <div className="mb-6 lg:mb-8" data-id="element-283">
        <span className="text-[15px] tracking-wider tabular-nums color-accent font-medium" data-id="element-284">
          {String(i + 1).padStart(2, '0')}.
        </span>
      </div>

      {/* Hero label */}
      <h3 className="color-ink-primary mb-5 lg:mb-6 text-[28px] font-[500]" style={{
      fontFamily: 'var(--font-display)',
      lineHeight: 1.05,
      letterSpacing: '-0.03em'
    }} data-id="element-285">
        {(() => {
        const parts = d.label.split(' ');
        const last = parts.pop() ?? '';
        const first = parts.join(' ');
        return <>
              {first}
              <br data-id="element-286" />
              {last}
            </>;
      })()}
      </h3>

      {/* Cost items list */}
      <ul className="flex flex-col border-t border-default" data-id="element-287">
        {d.description.map((chip, idx) => <motion.li key={chip} initial={{
        opacity: 0,
        y: 4
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.3,
        delay: idx * 0.04
      }} data-id="element-288">
            <button type="button" onClick={() => onChipClick(d.label, chip)} className="group w-full flex items-center justify-between gap-3 py-3 border-b border-default text-left text-body-sm color-ink-primary focus:outline-none cursor-pointer" aria-label={`Scopri di pi\u00f9 su ${chip}`} data-id="element-289">
              <span className="flex items-center gap-3" data-id="element-290">
                <span className="text-mono-tiny tabular-nums color-ink-tertiary" data-id="element-291">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <span className="transition-colors duration-200 group-hover:color-accent text-[14px]" data-id="element-292">
                  {chip}
                </span>
              </span>
              <span aria-hidden="true" className="color-ink-tertiary" data-id="element-293">
                →
              </span>
            </button>
          </motion.li>)}
      </ul>
    </motion.article>;
}
interface MobileCardProps {
  d: CostDimension;
  i: number;
  onChipClick: (category: string, chip: string) => void;
}
function MobileCard({
  d,
  i,
  onChipClick
}: MobileCardProps) {
  const tint = tints[i] || tints[0];
  return <article style={{
    background: tint.background,
    borderColor: tint.borderColor,
    boxShadow: tint.boxShadow
  }} className="rounded-2xl border p-6 sm:p-8 flex flex-col" data-id="element-294">
      <div className="mb-8" data-id="element-295">
        <span className="text-[15px] tracking-wider tabular-nums color-accent font-medium" data-id="element-296">
          {String(i + 1).padStart(2, '0')}.
        </span>
      </div>

      <h3 className="color-ink-primary mb-6" style={{
      fontFamily: 'var(--font-display)',
      fontSize: 'clamp(26px, 7vw, 36px)',
      fontWeight: 500,
      lineHeight: 1.05,
      letterSpacing: '-0.03em'
    }} data-id="element-297">
        {(() => {
        const parts = d.label.split(' ');
        const last = parts.pop() ?? '';
        const first = parts.join(' ');
        return <>
              {first}
              <br data-id="element-298" />
              {last}
            </>;
      })()}
      </h3>

      <ul className="flex flex-col border-t border-default" data-id="element-299">
        {d.description.map((chip, idx) => <li key={chip} data-id="element-300">
            <button type="button" onClick={() => onChipClick(d.label, chip)} className="group w-full flex items-center justify-between gap-3 py-3 border-b border-default text-left text-body-sm color-ink-primary focus:outline-none cursor-pointer" aria-label={`Scopri di pi\u00f9 su ${chip}`} data-id="element-301">
              <span className="flex items-center gap-3" data-id="element-302">
                <span className="text-mono-tiny tabular-nums color-ink-tertiary" data-id="element-303">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <span className="transition-colors duration-200 group-hover:color-accent" data-id="element-304">
                  {chip}
                </span>
              </span>
              <span aria-hidden="true" className="color-ink-tertiary" data-id="element-305">
                →
              </span>
            </button>
          </li>)}
      </ul>
    </article>;
}
interface ChipModalProps {
  active: ActiveChip;
  onClose: () => void;
}
function ChipModal({
  active,
  onClose
}: ChipModalProps) {
  // Chiusura con tasto Escape
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, onClose]);
  // Lock body scroll
  useEffect(() => {
    if (!active) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [active]);
  return <AnimatePresence data-id="element-306">
      {active && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} role="dialog" aria-modal="true" aria-labelledby="chip-modal-title" data-id="element-307">
          {/* Backdrop */}
          <button type="button" aria-label="Chiudi" onClick={onClose} className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-308" />

          {/* Card */}
          <motion.div initial={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative w-full max-w-[520px] rounded-2xl border border-default bg-[#0a0a0a] p-6 sm:p-8 shadow-[0_24px_80px_rgba(0,0,0,0.6)]" data-id="element-309">
            <button type="button" onClick={onClose} className="absolute top-4 right-4 inline-flex items-center justify-center w-9 h-9 rounded-full border border-default bg-elev1 hover:border-[var(--accent-500)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-500)] focus:ring-offset-2 focus:ring-offset-black transition-colors" aria-label="Chiudi" data-id="element-310">
              <X className="w-4 h-4" aria-hidden="true" data-id="element-311" />
            </button>

            <div className="text-mono-tiny tracking-wider color-ink-tertiary uppercase mb-4" data-id="element-312">
              {active.category}
            </div>

            <h3 id="chip-modal-title" className="color-ink-primary mb-4 text-[26px] sm:text-[28px] font-[500] pr-10" style={{
          fontFamily: 'var(--font-display)',
          lineHeight: 1.1,
          letterSpacing: '-0.02em'
        }} data-id="element-313">
              {active.chip}
            </h3>

            <p className="text-body-lg color-ink-secondary leading-relaxed" data-id="element-314">
              {chipExplanations[active.chip] ?? 'Spiegazione non ancora disponibile per questa voce.'}
            </p>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Problem() {
  const [active, setActive] = useState<ActiveChip>(null);
  const handleChipClick = (category: string, chip: string) => {
    setActive({
      category,
      chip
    });
  };
  return <section className="section-pad relative -mt-[40vh] md:-mt-[50vh] z-10 !pt-[80px] !pb-0" id="problema" style={{
    background: 'linear-gradient(to bottom, transparent 0%, var(--surface-base) 40vh, var(--surface-base) 100%)'
  }} data-id="element-315">
      <div className="wrap relative z-10" data-id="element-316">
        {/* ───────── TEMPO 1 — INTRO ───────── */}
        <AnimatedSection data-id="element-317">
          <div className="section-tag section-tag--accent mb-6" data-id="element-318">
            Il problema
          </div>
          <h2 className="text-display-section mb-6 w-full color-ink-primary text-[40px] text-white" data-id="element-319">
            Il costo di una delivery{' '}
            <strong className="font-medium text-white" data-id="element-320">mal gestita</strong>, è
            più alto di quanto pensi.
          </h2>
          <p className="text-body-xl color-ink-secondary max-w-[64ch]" data-id="element-321">
            Si manifesta su più piani: nei processi che rallentano e nel margine
            che si assottiglia giorno dopo giorno, nei clienti che dopo una
            brutta esperienza smettono semplicemente di tornare, e nelle voci
            che scopri — quasi sempre troppo tardi — solo tra le righe della
            fattura del corriere.
          </p>
        </AnimatedSection>

        {/* ───────── TEMPO 2 — LE TRE DIMENSIONI (grid) ───────── */}
        <div className="mt-[var(--s-8)] md:mt-[var(--s-10)]" data-id="element-322">
          {/* Desktop: side-by-side grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-5 lg:gap-6 items-stretch" data-id="element-323">
            {dimensions.map((d, i) => <ProblemCard key={d.index} d={d} i={i} onChipClick={handleChipClick} data-id="element-324" />)}
          </div>

          {/* Mobile: stacked cards in normal flow */}
          <div className="md:hidden flex flex-col gap-5" data-id="element-325">
            {dimensions.map((d, i) => <MobileCard key={d.index} d={d} i={i} onChipClick={handleChipClick} data-id="element-326" />)}
          </div>
        </div>
      </div>

      {/* Modale di spiegazione del costo */}
      <ChipModal active={active} onClose={() => setActive(null)} data-id="element-327" />
    </section>;
}